import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Container, 
    Card, 
    Table, 
    Button, 
    Form, 
    Alert, 
    Spinner, 
    Pagination, 
    Modal,
    Row,
    Col
} from 'react-bootstrap';
import { FaSearch, FaEye, FaCheckCircle, FaTimesCircle, FaPlusCircle } from 'react-icons/fa';

// Asegúrate de que esta URL sea la correcta para tu API
const API_URL = "https://mixta39.dev/phpapi/api/estudiantes.php";

const Estudiantes = () => {
  const navigate = useNavigate();
  const [estudiantes, setEstudiantes] = useState([]);
  const [filteredEstudiantes, setFilteredEstudiantes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [estudiantesPerPage] = useState(35);
  const [successMessage, setSuccessMessage] = useState("");
  const [turnoFilter, setTurnoFilter] = useState("");
  const [gradoFilter, setGradoFilter] = useState("");
  const [grupoFilter, setGrupoFilter] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  
  const initialNewEstudianteState = {
    Nombre: '',
    ApellidoPaterno: '',
    ApellidoMaterno: '',
    CURP: '',
    Edad: '',
    Grado: '',
    Grupo: '',
    Email: '',
    Telefono: '',
    Turno: '',
  };
  const [newEstudiante, setNewEstudiante] = useState(initialNewEstudianteState);

  // Verifica el token al cargar el componente
  useEffect(() => {
    const token = localStorage.getItem("user");
    if (!token) {
      navigate("/login");
    }
  }, [navigate]);
  
  // Carga los estudiantes cuando los filtros cambian
  useEffect(() => {
    obtenerEstudiantes();
  }, [turnoFilter, gradoFilter, grupoFilter]);

  // Obtiene los estudiantes desde la API
  const obtenerEstudiantes = async () => {
    setLoading(true);
    setError("");
    try {
      const url = `${API_URL}?turno=${turnoFilter}&grado=${gradoFilter}&grupo=${grupoFilter}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error("No se pudo obtener la lista de estudiantes.");
      }
      const data = await response.json();
      setEstudiantes(data);
      setFilteredEstudiantes(data);
    } catch (error) {
      console.error('Error al obtener los estudiantes:', error);
      setError("Hubo un error al cargar los estudiantes. Intenta de nuevo más tarde.");
    } finally {
      setLoading(false);
    }
  };
  
  // Aplica el filtro de búsqueda por texto sobre la lista actual
  useEffect(() => {
    const lowercasedFilter = searchTerm.toLowerCase();
    const filteredData = estudiantes.filter(estudiante =>
      `${estudiante.Nombre} ${estudiante.ApellidoPaterno} ${estudiante.ApellidoMaterno} ${estudiante.Email}`
        .toLowerCase()
        .includes(lowercasedFilter)
    );
    setFilteredEstudiantes(filteredData);
    setCurrentPage(1);
  }, [searchTerm, estudiantes]);

  // Lógica de paginación
  const indexOfLastEstudiante = currentPage * estudiantesPerPage;
  const indexOfFirstEstudiante = indexOfLastEstudiante - estudiantesPerPage;
  const currentEstudiantes = filteredEstudiantes.slice(indexOfFirstEstudiante, indexOfLastEstudiante);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredEstudiantes.length / estudiantesPerPage); i++) {
    pageNumbers.push(i);
  }

  // Navega a la vista de detalles del estudiante
  const handleViewDetails = (EstudianteID) => {
    navigate(`/estudiantes/detalles/${EstudianteID}`);
  };
  
  // Cambia el estado (Activo/Inactivo) y envía el ID en la URL
  const toggleHabilitado = async (EstudianteID, currentStatus) => {
    const studentToUpdate = estudiantes.find(est => est.EstudianteID === EstudianteID);

    if (!studentToUpdate) {
        console.error("Estudiante no encontrado.");
        setError("Ocurrió un error al intentar actualizar.");
        return;
    }

    const newStatus = !currentStatus;
    const updatedStudentPayload = { ...studentToUpdate, Activo: newStatus };
    
    const url = `${API_URL}?EstudianteID=${EstudianteID}`;
    
    const { EstudianteID: _, ...bodyPayload } = updatedStudentPayload;
    
    try {
        const response = await fetch(url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(bodyPayload)
        });

        if (!response.ok) {
            throw new Error('Falló la actualización en el servidor.');
        }

        const updatedEstudiantes = estudiantes.map(est =>
            est.EstudianteID === EstudianteID ? updatedStudentPayload : est
        );
        setEstudiantes(updatedEstudiantes);
        setSuccessMessage("Estado del estudiante actualizado correctamente.");
        setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
        setError("No se pudo actualizar el estado del estudiante.");
        setTimeout(() => setError(""), 3000);
    }
  };
  
  // Maneja el envío del formulario para agregar un nuevo estudiante
  const handleAddEstudiante = () => {
    if (!newEstudiante.Nombre || !newEstudiante.ApellidoPaterno || !newEstudiante.Grado || !newEstudiante.Grupo || !newEstudiante.Turno || !newEstudiante.CURP) {
        setError("Por favor, completa todos los campos requeridos.");
        return;
    }

    const userString = localStorage.getItem("user");
    if (!userString) {
        setError("Error de autenticación. Por favor, inicia sesión de nuevo.");
        return;
    }
    
    const userData = JSON.parse(userString);
    const usuarioCreadorID = userData.id;

    const payload = {
      ...newEstudiante,
      EstudianteID: 0,
      UsuarioCreadorID: usuarioCreadorID,
    };

    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    .then(response => response.json())
    .then(data => {
        if (data.error) throw new Error(data.error);
        
        setSuccessMessage('Estudiante agregado correctamente.');
        setShowModal(false);
        setNewEstudiante(initialNewEstudianteState);
        obtenerEstudiantes();
        setTimeout(() => setSuccessMessage(""), 3000);
    })
    .catch(error => {
        setError(error.message || 'Hubo un error al agregar el estudiante.');
        setTimeout(() => setError(""), 3000);
    });
  };
  
  // Abre el modal y reinicia el estado del formulario
  const handleShowModal = () => {
    setNewEstudiante(initialNewEstudianteState);
    setError("");
    setShowModal(true);
  };
  
  // Se ajusta la función para convertir la CURP a mayúsculas
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const finalValue = name === 'CURP' ? value.toUpperCase() : value;
    setNewEstudiante({ ...newEstudiante, [name]: finalValue });
  };

  return (
    <Container className="mt-5">
      <Card className="shadow p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2>Lista de Estudiantes</h2>
          <Button variant="primary" onClick={handleShowModal}>
            <FaPlusCircle className="me-2" />Agregar Estudiante
          </Button>
        </div>

        {/* Filtros */}
        <Row className="mb-3">
            <Col md={4}>
                <Form.Control as="select" value={turnoFilter} onChange={(e) => setTurnoFilter(e.target.value)}>
                    <option value="">Todos los Turnos</option>
                    <option value="Matutino">Matutino</option>
                    <option value="Vespertino">Vespertino</option>
                </Form.Control>
            </Col>
            <Col md={4}>
                <Form.Control as="select" value={gradoFilter} onChange={(e) => setGradoFilter(e.target.value)}>
                    <option value="">Todos los Grados</option>
                    <option value="Primero">Primero</option>
                    <option value="Segundo">Segundo</option>
                    <option value="Tercero">Tercero</option>
                </Form.Control>
            </Col>
            <Col md={4}>
                <Form.Control as="select" value={grupoFilter} onChange={(e) => setGrupoFilter(e.target.value)}>
                    <option value="">Todos los Grupos</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                </Form.Control>
            </Col>
        </Row>
        
        {/* Búsqueda */}
        <div className="mb-3 d-flex align-items-center">
          <FaSearch className="me-2 text-muted" />
          <Form.Control
            type="text"
            placeholder="Buscar por nombre, apellido o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Alertas */}
        {successMessage && <Alert variant="success" onClose={() => setSuccessMessage("")} dismissible><FaCheckCircle className="me-2" />{successMessage}</Alert>}
        {error && <Alert variant="danger" onClose={() => setError("")} dismissible><FaTimesCircle className="me-2" />{error}</Alert>}

        {/* Indicador de carga */}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Cargando estudiantes...</p>
          </div>
        ) : (
          <>
            <Table striped bordered hover responsive>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Nombre Completo</th>
                  <th>Turno</th>
                  <th>Grado</th>
                  <th>Grupo</th>
                  <th>Email</th>
                  <th>Teléfono</th>
                  <th>Estado</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentEstudiantes.length > 0 ? (
                    currentEstudiantes.map((estudiante, index) => (
                    <tr key={estudiante.EstudianteID}>
                      <td>{indexOfFirstEstudiante + index + 1}</td>
                      <td>{`${estudiante.ApellidoPaterno} ${estudiante.ApellidoMaterno} ${estudiante.Nombre}`}</td>
                      <td>{estudiante.Turno}</td>
                      <td>{estudiante.Grado}</td>
                      <td>{estudiante.Grupo}</td>
                      <td>{estudiante.Email}</td>
                      <td>{estudiante.Telefono}</td>
                      <td>
                        <Button
                          variant={estudiante.Activo ? "success" : "danger"}
                          size="sm"
                          onClick={() => toggleHabilitado(estudiante.EstudianteID, estudiante.Activo)}
                          title={estudiante.Activo ? "Desactivar" : "Activar"}
                        >
                          {estudiante.Activo ? <FaCheckCircle /> : <FaTimesCircle />}
                        </Button>
                      </td>
                      <td>
                        <Button variant="outline-primary" size="sm" onClick={() => handleViewDetails(estudiante.EstudianteID)}>
                          <FaEye className="me-1" /> Ver Detalles
                        </Button>
                      </td>
                    </tr>
                    ))
                ) : (
                    <tr>
                        <td colSpan="9" className="text-center">No se encontraron estudiantes.</td>
                    </tr>
                )}
              </tbody>
            </Table>

            {/* Paginación */}
            {filteredEstudiantes.length > estudiantesPerPage && (
                <div className="d-flex justify-content-center">
                    <Pagination>
                    {pageNumbers.map((number) => (
                        <Pagination.Item key={number} active={number === currentPage} onClick={() => paginate(number)}>
                        {number}
                        </Pagination.Item>
                    ))}
                    </Pagination>
                </div>
            )}
          </>
        )}
      </Card>

      {/* Modal para agregar estudiante */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Agregar Nuevo Estudiante</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              {/* Columna Izquierda */}
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formNombre">
                  <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                  <Form.Control type="text" name="Nombre" value={newEstudiante.Nombre} onChange={handleInputChange} required />
                </Form.Group>
                
                <Form.Group className="mb-3" controlId="formApellidoPaterno">
                  <Form.Label>Apellido Paterno <span className="text-danger">*</span></Form.Label>
                  <Form.Control type="text" name="ApellidoPaterno" value={newEstudiante.ApellidoPaterno} onChange={handleInputChange} required />
                </Form.Group>
                
                <Form.Group className="mb-3" controlId="formApellidoMaterno">
                  <Form.Label>Apellido Materno</Form.Label>
                  <Form.Control type="text" name="ApellidoMaterno" value={newEstudiante.ApellidoMaterno} onChange={handleInputChange} />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formCURP">
                  <Form.Label>CURP <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="CURP"
                    value={newEstudiante.CURP}
                    onChange={handleInputChange}
                    required
                    maxLength="18"
                  />
                </Form.Group>
                
                <Form.Group className="mb-3" controlId="formEmail">
                  <Form.Label>Email</Form.Label>
                  <Form.Control type="email" name="Email" value={newEstudiante.Email} onChange={handleInputChange} />
                </Form.Group>
              </Col>
              
              {/* Columna Derecha */}
              <Col md={6}>
                <Form.Group className="mb-3" controlId="formEdad">
                  <Form.Label>Edad</Form.Label>
                  <Form.Control type="number" name="Edad" value={newEstudiante.Edad} onChange={handleInputChange} />
                </Form.Group>
                
                <Form.Group className="mb-3" controlId="formTelefono">
                  <Form.Label>Teléfono</Form.Label>
                  <Form.Control type="text" name="Telefono" value={newEstudiante.Telefono} onChange={handleInputChange} />
                </Form.Group>

                <Form.Group className="mb-3" controlId="formModalGrado">
                    <Form.Label>Grado <span className="text-danger">*</span></Form.Label>
                    <Form.Control as="select" name="Grado" value={newEstudiante.Grado} onChange={handleInputChange} required>
                        <option value="">Seleccionar Grado...</option>
                        <option value="Primero">Primero</option>
                        <option value="Segundo">Segundo</option>
                        <option value="Tercero">Tercero</option>
                    </Form.Control>
                </Form.Group>

                <Form.Group className="mb-3" controlId="formModalGrupo">
                    <Form.Label>Grupo <span className="text-danger">*</span></Form.Label>
                    <Form.Control as="select" name="Grupo" value={newEstudiante.Grupo} onChange={handleInputChange} required>
                        <option value="">Seleccionar Grupo...</option>
                        <option value="A">A</option>
                        <option value="B">B</option>
                        <option value="C">C</option>
                    </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col>
                <Form.Group className="mb-3" controlId="formModalTurno">
                    <Form.Label>Turno <span className="text-danger">*</span></Form.Label>
                    <Form.Control as="select" name="Turno" value={newEstudiante.Turno} onChange={handleInputChange} required>
                        <option value="">Seleccionar Turno...</option>
                        <option value="Matutino">Matutino</option>
                        <option value="Vespertino">Vespertino</option>
                    </Form.Control>
                </Form.Group>
              </Col>
            </Row>
            {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleAddEstudiante}>
            Guardar Estudiante
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Estudiantes;